import type { User } from 'firebase/auth';
import { onIdTokenChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { createContext, type ReactNode, useEffect, useRef, useState } from 'react';
import { auth, db } from '@/services';
import type { AppUser } from '@/types';
import { notifyLiveDataError } from '@/utils';
import { createStartupSnapshotLogger, logStartupTiming } from '@/utils/startupPerformance';

export interface AuthContextValue {
  /** The raw Firebase Auth user, or `null` if signed out. */
  firebaseUser: User | null;
  /** The corresponding `users/{uid}` Firestore document, once loaded. */
  appUser: AppUser | null;
  /**
   * Whether the signed-in user holds the `admin` custom claim. This is the
   * only value that should ever gate access to admin features — never
   * `appUser.isAdmin`, which is an informational mirror only.
   */
  isAdmin: boolean;
  /**
   * True until the initial auth state and (if signed in) the `users/{uid}`
   * profile doc have been resolved. Deliberately does *not* wait on the
   * `admin` claim fetch below — most routes (anything gated by
   * `ProtectedRoute`, not `AdminRoute`) don't need `isAdmin` to render, so
   * making them wait on it just adds a serialized network round-trip to
   * every page load for no reason.
   */
  loading: boolean;
  /**
   * True until the `admin` custom claim has been resolved for the current
   * `firebaseUser`. Only `AdminRoute` needs to gate on this (its
   * allow/redirect decision depends on `isAdmin`); everything else should
   * use `loading` instead so it isn't held up by this fetch.
   */
  isAdminLoading: boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  const [isAdminLoading, setIsAdminLoading] = useState(true);
  const localAdminLoginAttempted = useRef(false);
  const initialAuthResolved = useRef(false);

  // Tracks auth state. `onIdTokenChanged` (not just `onAuthStateChanged`)
  // also fires whenever the ID token is refreshed, which is what picks up a
  // claim change after `setAdminRole` + a forced token refresh. `firebaseUser`
  // and `authLoading` are resolved synchronously from the callback itself so
  // that `ProtectedRoute`-gated pages (the common case) can render as soon as
  // auth state is known, without waiting on the separate claims fetch below.
  useEffect(() => {
    const startedAt = performance.now();
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      const useLocalAdminBypass =
        import.meta.env.DEV &&
        import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true' &&
        import.meta.env.VITE_LOCAL_ADMIN_BYPASS === 'true' &&
        import.meta.env.VITE_FIREBASE_PROJECT_ID === 'demo-route-planner-local';

      if (useLocalAdminBypass && !user && !localAdminLoginAttempted.current) {
        localAdminLoginAttempted.current = true;
        setAuthLoading(true);
        const localAdminEmail = import.meta.env.VITE_LOCAL_ADMIN_EMAIL;
        const localAdminPassword = import.meta.env.VITE_LOCAL_ADMIN_PASSWORD;
        if (!(localAdminEmail && localAdminPassword)) {
          notifyLiveDataError(
            'AuthContext (local emulator sign-in)',
            new Error('Local admin credentials are not configured.'),
          );
          setAuthLoading(false);
          setIsAdminLoading(false);
          return;
        }

        void signInWithEmailAndPassword(auth, localAdminEmail, localAdminPassword).catch(
          (error: unknown) => {
            notifyLiveDataError('AuthContext (local emulator sign-in)', error);
            setAuthLoading(false);
            setIsAdminLoading(false);
          },
        );
        return;
      }

      if (!initialAuthResolved.current) {
        logStartupTiming('auth-state', startedAt, { signedIn: user !== null });
        initialAuthResolved.current = true;
      }

      setFirebaseUser(user);
      setAuthLoading(false);

      if (!user) {
        setIsAdmin(false);
        setIsAdminLoading(false);
        return;
      }

      setIsAdminLoading(true);
      user
        .getIdTokenResult()
        .then((tokenResult) => setIsAdmin(tokenResult.claims.admin === true))
        .catch((error) => {
          notifyLiveDataError('AuthContext (token claims)', error);
          setIsAdmin(false);
        })
        .finally(() => setIsAdminLoading(false));
    });
    return unsubscribe;
  }, []);

  // Keeps the `users/{uid}` profile doc in sync while signed in.
  useEffect(() => {
    if (!firebaseUser) {
      setAppUser(null);
      setProfileLoading(false);
      return;
    }

    const startedAt = performance.now();
    const logProfileSnapshot = createStartupSnapshotLogger('profile');
    setProfileLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, 'users', firebaseUser.uid),
      (snap) => {
        logProfileSnapshot({
          fromCache: snap.metadata.fromCache,
          resultCount: snap.exists() ? 1 : 0,
        });

        // Derive `uid` from the doc id (== firebaseUser.uid) rather than
        // trusting the `uid` field — some user docs predate that field
        // being written and lack it, which broke every `appUser.uid`
        // comparison (e.g. "is this route mine?" on the Today screen).
        setAppUser(snap.exists() ? { ...(snap.data() as AppUser), uid: snap.id } : null);
        setProfileLoading(false);
      },
      (error) => {
        logStartupTiming('profile-first-snapshot-error', startedAt, { failed: true });
        notifyLiveDataError('AuthContext (profile)', error);
        setProfileLoading(false);
      },
    );
    return unsubscribe;
  }, [firebaseUser]);

  const loading = authLoading || (firebaseUser !== null && profileLoading);

  return (
    <AuthContext.Provider value={{ firebaseUser, appUser, isAdmin, loading, isAdminLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
